import { HttpAction, HttpService } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";

import { unitTemplateCategories } from "./unitTemplate";

export const unitGraphNodeKinds = ["top", "template", "input", "filler"] as const;
export type UnitGraphNodeKind = (typeof unitGraphNodeKinds)[number];

export type UnitGraphNode = {
  unit: string;
  kind: UnitGraphNodeKind;
  templateId: string | null;
  quantity: number;
  children: UnitGraphNode[];
};

export const unitGraphSerializer = Serializers.object<UnitGraphNode>({
  unit: Serializers.string,
  kind: Serializers.stringOneOf<UnitGraphNodeKind>(...unitGraphNodeKinds),
  templateId: Serializers.string.nullable(),
  quantity: Serializers.integer,
  children: Serializers.identity(),
});
unitGraphSerializer.fields.children = Serializers.array(unitGraphSerializer);

export const calculatorTemplateSerializer = Serializers.object({
  id: Serializers.string,
  unit: Serializers.string,
  category: Serializers.stringOneOf(...unitTemplateCategories),
  label: Serializers.string,
  complexity: Serializers.integer,
  components: Serializers.record(Serializers.integer),
});
export type CalculatorTemplate = SerializerValue<typeof calculatorTemplateSerializer>;

export const calculatorContextSerializer = Serializers.object({
  templates: calculatorTemplateSerializer.array(),
  inputUnits: Serializers.record(Serializers.integer),
  topCosts: Serializers.record(Serializers.integer),
  fillerCosts: Serializers.record(Serializers.integer),
});
export type CalculatorContext = SerializerValue<typeof calculatorContextSerializer>;

export const calculatorOutputSerializer = Serializers.object({
  graphs: unitGraphSerializer.array(),
});
export type CalculatorOutput = SerializerValue<typeof calculatorOutputSerializer>;

export const solutionSerializer = Serializers.object({
  totalCost: Serializers.decimal,
  topCost: Serializers.decimal,
  fillerCost: Serializers.decimal,
  topUnits: Serializers.object({
    unit: Serializers.string,
    quantity: Serializers.integer,
    cost: Serializers.decimal,
    topCost: Serializers.decimal,
    fillerCost: Serializers.decimal,
    graph: unitGraphSerializer,

    totalItems: Serializers.record(Serializers.integer),
    totalFiller: Serializers.record(Serializers.integer),

    unitItems: Serializers.record(Serializers.integer),
    unitFiller: Serializers.record(Serializers.integer),
    unitFillerCosts: Serializers.record(Serializers.decimal),
  }).array(),
});

export type ShippingSolution = SerializerValue<typeof solutionSerializer>;

export const calculateInputSerializer = Serializers.object({
  items: Serializers.object({
    itemId: Serializers.string,
    quantity: Serializers.integer,
  }).array(),
  shippingRates: Serializers.record(Serializers.decimal),
  fillerCosts: Serializers.record(Serializers.decimal),
});
export type CalculateInput = SerializerValue<typeof calculateInputSerializer>;

export const calculateActions = HttpService({
  calculate: HttpAction({
    method: "post",
    path: "/calculate",
    bodySerializer: calculateInputSerializer,
    errorSerializer: Serializers.stringOneOf("NoSolution"),
    valueSerializer: solutionSerializer,
  }),
});

export const unitGraphToString = (unitGraph: UnitGraphNode, level = 0): string => {
  const indentWidth = level * 2 + 1;

  let thisLine = unitGraph.quantity.toString();

  if (unitGraph.kind === "input") {
    thisLine = "+" + thisLine;
  } else if (unitGraph.kind === "filler") {
    thisLine = "'" + thisLine;
  }

  thisLine = thisLine.padStart(4) + " ".repeat(indentWidth);
  thisLine += unitGraph.unit;

  const childLines = unitGraph.children.map((c) => unitGraphToString(c, level + 1));

  return [thisLine, ...childLines].join("\n");
};
